import { useRef, useState, useMemo, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { Button, Collapse, InputNumber, Segmented, Select, Space, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useReduxMetricDropdown } from "../shared/useReduxMetricDropdown";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import { MetricRow } from "./MetricRow";
import { SubjectRow, type SubjectEntityType } from "./SubjectRow";
import { buildMockRadarOptions, type MockSeriesConfig } from "./radarMockOptions";
import type { RadarComponentSpec } from "../../Processing/types";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { PreviewShell } from "../shared/PreviewShell";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";
import { DateRangeForm } from "../shared/DateRangeForm";
import { resolveDateRange } from "../../Utils/resolveDateRange";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { isSectionBindable } from "../../Processing/controllerBindings";
import { SectionSummary } from "../shared/SectionSummary";
import {
  radarReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  AGG_PREFIX,
  type RadarMetricEntry,
  type RadarVertexDimension,
} from "./radarState";

HighchartsMore(Highcharts);

const { Panel } = Collapse;
const { Text } = Typography;
const { OptGroup, Option } = Select;

const MAX_SERIES = 10;
const MAX_VERTICES = 8;
const MIN_METRICS = 3;
const MIN_ENTITY_VERTICES = 3;
const SERIES_COLORS = [
  "#4d7cf7", "#f7874d", "#4df7a8", "#f7d24d", "#c44df7",
  "#f74d7b", "#4df7f0", "#a3f74d", "#f7974d", "#7b4df7",
];

const VERTEX_OPTIONS: { label: string; value: RadarVertexDimension }[] = [
  { label: "Metrics",   value: "metric"   },
  { label: "Players",   value: "player"   },
  { label: "Positions", value: "position" },
  { label: "Drills",    value: "drill"    },
];


// ── Scale helper text ──────────────────────────────────────────

function getScaleHelperText(normalization: string, dim: RadarVertexDimension): string {
  switch (normalization) {
    // % Of Max is only offered for metric vertices (hidden for entity vertices).
    case "player_minmax":
      return "Each metric axis is scored 0–100 based on the player's own historical min–max.";
    case "team_zscore":
      return "Values are z-scored across the team — average performance sits at the midpoint.";
    case "custom":
      return dim === "metric"
        ? "Set each metric's min–max in its own units; the spoke fills 0–100 across that range."
        : "A fixed min–max is applied to the metric scale, independent of historical data.";
    default:
      return "";
  }
}

// ── Main configurator ──────────────────────────────────────────

export function RadarConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps) {
  const [state, dispatch] = useConfiguratorSync({
    reducer: radarReducer,
    existingSpec: existingSpec as RadarComponentSpec | undefined,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  // ── Redux metric dropdown ──
  const { metricDropdown } = useReduxMetricDropdown({ includeHelmet: true });

  // Returns the display label for a metricId (without agg prefix)
  const getMetricBaseName = (metricId: string | null): string => {
    if (!metricId) return "";
    for (const g of metricDropdown) {
      const opt = g.options?.find((o: any) => o.value === metricId);
      if (opt) return opt.displayLabel ?? opt.label;
    }
    return metricId;
  };

  // ── Derived state ──

  const isEntityVertex = state.vertexDimension !== "metric";
  const configuredMetrics = state.metrics.filter((m) => m.metricId != null);

  const tooFewVertices = isEntityVertex
    ? state.subjects.length < MIN_ENTITY_VERTICES
    : configuredMetrics.length < MIN_METRICS;

  const tooFewSeries = isEntityVertex
    ? configuredMetrics.length < 1
    : false;

  const dateLabel = resolveDateRange(state);

  // Step header summaries
  const vertexSummary = useMemo(() => {
    if (!isEntityVertex) {
      if (configuredMetrics.length === 0) return null;
      if (configuredMetrics.length === 1) return getMetricBaseName(configuredMetrics[0].metricId);
      return `${configuredMetrics.length} metrics`;
    }
    if (state.subjects.length === 0) return null;
    if (state.subjects.length === 1) return state.subjects[0].displayNameOverride ?? state.subjects[0].displayName;
    return `${state.subjects.length} ${state.vertexDimension}s`;
  }, [isEntityVertex, configuredMetrics, state.subjects, state.vertexDimension]);

  const seriesSummary = useMemo(() => {
    if (isEntityVertex) {
      if (configuredMetrics.length === 0) return null;
      if (configuredMetrics.length === 1) return getMetricBaseName(configuredMetrics[0].metricId);
      return `${configuredMetrics.length} metrics`;
    }
    return `${state.subjects.length} series`;
  }, [isEntityVertex, configuredMetrics, state.subjects.length]);

  // Entity types allowed in vertex picker (no "team" / "full-team" as an axis label)
  const vertexAllowedTypes: SubjectEntityType[] = useMemo(() => {
    switch (state.vertexDimension) {
      case "player":   return ["player", "group"];
      case "position": return ["position"];
      case "drill":    return ["drill"];
      default:         return [];
    }
  }, [state.vertexDimension]);

  // Series subject allowed types (all, including team)
  const seriesAllowedTypes: SubjectEntityType[] = ["team", "player", "position", "group"];

  // ── Mock preview ──

  const mockVertexLabels = useMemo(() => {
    if (!isEntityVertex) {
      if (configuredMetrics.length < MIN_METRICS) return [];
      return configuredMetrics.map((m) => m.displayNameOverride ?? getMetricBaseName(m.metricId));
    }
    if (state.subjects.length < MIN_ENTITY_VERTICES) return [];
    return state.subjects.map((s) => s.displayNameOverride ?? s.displayName);
  }, [isEntityVertex, configuredMetrics, state.subjects]);

  const mockSeriesConfig = useMemo((): MockSeriesConfig[] => {
    if (isEntityVertex) {
      if (configuredMetrics.length === 0) {
        return [{ name: "Series 1", color: SERIES_COLORS[0], fill: false }];
      }
      return configuredMetrics.map((m, i) => ({
        name: m.displayNameOverride ?? `${AGG_PREFIX[m.metricAgg]} ${getMetricBaseName(m.metricId)}`,
        color: m.colorOverride || SERIES_COLORS[i % SERIES_COLORS.length],
        fill: m.fillArea ?? false,
      }));
    }
    return state.subjects.map((s, i) => ({
      name: (s.displayNameOverride ?? s.displayName) || `Series ${i + 1}`,
      color: s.colorOverride || SERIES_COLORS[i % SERIES_COLORS.length],
      fill: s.fillArea ?? false,
    }));
  }, [isEntityVertex, configuredMetrics, state.subjects]);

  const mockRadarOptions = useMemo(
    () =>
      mockVertexLabels.length >= 3
        ? buildMockRadarOptions(mockVertexLabels, mockSeriesConfig, state.showLegend)
        : null,
    [mockVertexLabels, mockSeriesConfig, state.showLegend],
  );

  // ── Vertex step description ──
  const vertexStepLabel = isEntityVertex
    ? state.vertexDimension.charAt(0).toUpperCase() + state.vertexDimension.slice(1) + "s"
    : "Metrics";

  const seriesStepLabel = isEntityVertex ? "Metrics" : "Series";

  // ── Metric auto-population ──

  const buildFirst5Metrics = (): RadarMetricEntry[] => {
    const opts = metricDropdown.flatMap((g: any) => g.options ?? []).slice(0, 5);
    const entries: RadarMetricEntry[] = opts.map((opt: any) => ({
      id: crypto.randomUUID(),
      metricId: opt.value as string,
      metricAgg: "avg" as const,
      metricIdentifierType: "metric" as const,
    }));
    while (entries.length < MIN_METRICS) {
      entries.push({ id: crypto.randomUUID(), metricId: null, metricAgg: "avg", metricIdentifierType: "metric" });
    }
    return entries;
  };

  // Populate first 5 metrics once on new chart creation (skip when editing).
  const hasAutoPopulatedRef = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (existingSpec) return;
    if (hasAutoPopulatedRef.current) return;
    if (!metricDropdown.some((g: any) => g.options?.length > 0)) return;
    hasAutoPopulatedRef.current = true;
    dispatch({ type: "POPULATE_INITIAL_METRICS", metrics: buildFirst5Metrics() });
  }, [metricDropdown]);

  // ── Dimension change with entity auto-population ──

  const handleVertexDimensionChange = (v: RadarVertexDimension) => {
    const nMetrics = state.metrics.length;

    if (v === "metric") {
      dispatch({ type: "SET_VERTEX_DIMENSION", vertexDimension: "metric", metrics: buildFirst5Metrics() });
      return;
    }

    // Entity vertex: reset series to 1 metric (first available)
    const firstOpt = metricDropdown.flatMap((g: any) => g.options ?? [])[0];
    const singleMetric: RadarMetricEntry[] = [{
      id: crypto.randomUUID(),
      metricId: firstOpt?.value ?? null,
      metricAgg: "avg",
      metricIdentifierType: "metric",
    }];

    const prefixedEntities: Array<{ id: string; name: string; prefix: string }> = [];
    if (v === "player") {
      getSubjectEntities("player").forEach((e) => prefixedEntities.push({ ...e, prefix: "player" }));
      getSubjectEntities("group").forEach((e) => prefixedEntities.push({ ...e, prefix: "group" }));
    } else if (v === "position") {
      getSubjectEntities("position").forEach((e) => prefixedEntities.push({ ...e, prefix: "pos" }));
    } else if (v === "drill") {
      getSubjectEntities("drill").forEach((e) => prefixedEntities.push({ ...e, prefix: "drill" }));
    }

    const populated = prefixedEntities.slice(0, 5).map((e) => ({
      subjectFilter: `${e.prefix}-${e.id}`,
      displayName: e.name,
      queryIds: Array.from({ length: nMetrics }, () => crypto.randomUUID()),
    }));
    while (populated.length < MIN_ENTITY_VERTICES) {
      populated.push({ subjectFilter: "", displayName: "", queryIds: Array.from({ length: nMetrics }, () => crypto.randomUUID()) });
    }

    dispatch({ type: "SET_VERTEX_DIMENSION", vertexDimension: v, subjects: populated, metrics: singleMetric });
  };

  // ── Add/remove helpers ──

  const canAddVertex    = isEntityVertex ? state.subjects.length < MAX_VERTICES : true;
  const canAddSeries    = !isEntityVertex && state.subjects.length < MAX_SERIES;
  const minVertexRemove = isEntityVertex ? MIN_ENTITY_VERTICES : 0; // metric vertices use REMOVE_METRIC min

  // ── Form ──────────────────────────────────────────────────────

  const form = (
    <>
      {/* Title */}
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8ecf0" }}>
        <Space size={8} align="center" style={{ width: "100%" }}>
          <Text style={{ fontSize: 12, minWidth: 40 }}>Title</Text>
          <SlicerTokenInput
            size="small"
            placeholder="e.g. Player Workload Profile"
            value={state.title}
            onChange={(title) => dispatch({ type: "SET_TITLE", title })}
            style={{ flex: 1 }}
          />
        </Space>
      </div>

      <Collapse
        accordion
        activeKey={state.expandedKey}
        onChange={(key) => dispatch({ type: "SET_EXPANDED_KEY", key })}
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        {/* Step 1: Dimensions */}
        <Panel
          key="__dimensions"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">1</span>
              <Text strong style={{ fontSize: 13 }}>Dimensions</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Vertices: {vertexStepLabel} · Series: {seriesStepLabel}
              </Text>
            </div>
          }
        >
          <div className="awm-step-body">
            <Text type="secondary" style={{ fontSize: 11 }}>
              Choose what appears on the radar axes — the series lines are the other dimension.
            </Text>
            <Segmented
              size="small"
              options={VERTEX_OPTIONS}
              value={state.vertexDimension}
              onChange={(v) => handleVertexDimensionChange(v as RadarVertexDimension)}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {isEntityVertex
                ? `Each ${state.vertexDimension} becomes an axis; series lines are metrics.`
                : "Each metric becomes an axis; series lines are subjects."}
            </Text>
          </div>
        </Panel>

        {/* Step 2: Vertices */}
        <Panel
          key="__vertices"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">2</span>
              <Text strong style={{ fontSize: 13 }}>Vertices</Text>
              {vertexSummary && (
                <Text type="secondary" style={{ fontSize: 11 }}>{vertexSummary}</Text>
              )}
              {tooFewVertices && (
                <Text type="danger" style={{ fontSize: 11 }}>
                  min {isEntityVertex ? MIN_ENTITY_VERTICES : MIN_METRICS}
                </Text>
              )}
            </div>
          }
        >
          {isEntityVertex ? (
            // Entity vertex step: add/remove entity subjects as axes
            <div className="awm-step-body">
              <Text type="secondary" style={{ fontSize: 11 }}>
                Select at least {MIN_ENTITY_VERTICES} {state.vertexDimension}s — each becomes an axis.
              </Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {state.subjects.map((s, idx) => (
                  <SubjectRow
                    key={idx}
                    index={idx}
                    entry={s}
                    fallbackColor={SERIES_COLORS[idx % SERIES_COLORS.length]}
                    getSubjectEntities={getSubjectEntities}
                    canRemove={state.subjects.length > minVertexRemove}
                    allowedTypes={vertexAllowedTypes}
                    showSeriesControls={false}
                    onChange={(patch) => dispatch({ type: "UPDATE_SUBJECT", index: idx, patch })}
                    onRemove={() => dispatch({ type: "REMOVE_SUBJECT", index: idx })}
                  />
                ))}
              </div>
              {canAddVertex && (
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => dispatch({ type: "ADD_SUBJECT" })}
                  style={{ fontWeight: 600, color: "#1677ff", paddingLeft: 0 }}
                >
                  Add {state.vertexDimension.charAt(0).toUpperCase() + state.vertexDimension.slice(1)}
                </Button>
              )}
            </div>
          ) : (
            // Metric vertex step: metric rows as axes
            <div className="awm-step-body">
              <Text type="secondary" style={{ fontSize: 11 }}>
                Select at least {MIN_METRICS} metrics — each becomes an axis.
              </Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {state.metrics.map((entry, i) => (
                  <MetricRow
                    key={entry.id}
                    entry={entry}
                    index={i}
                    metricDropdown={metricDropdown}
                    canRemove={state.metrics.length > MIN_METRICS}
                    showSeriesControls={false}
                    seriesColor={SERIES_COLORS[i % SERIES_COLORS.length]}
                    getDefaultLabel={getMetricBaseName}
                    onChange={(patch) => dispatch({ type: "UPDATE_METRIC", index: i, patch })}
                    onRemove={() => dispatch({ type: "REMOVE_METRIC", index: i })}
                  />
                ))}
              </div>
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => dispatch({ type: "ADD_METRIC" })}
                style={{ fontWeight: 600, color: "#1677ff", paddingLeft: 0 }}
              >
                Add Metric
              </Button>
            </div>
          )}
        </Panel>

        {/* Step 3: Series */}
        <Panel
          key="__series"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">3</span>
              <Text strong style={{ fontSize: 13 }}>Series</Text>
              {seriesSummary && (
                <Text type="secondary" style={{ fontSize: 11 }}>{seriesSummary}</Text>
              )}
              {tooFewSeries && (
                <Text type="danger" style={{ fontSize: 11 }}>at least 1 required</Text>
              )}
            </div>
          }
        >
          {isEntityVertex ? (
            // Metric series: metric rows with color/fill/rename controls
            <div className="awm-step-body">
              <Text type="secondary" style={{ fontSize: 11 }}>
                Each metric is a line on the radar. Set a color, fill, or custom label.
              </Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {state.metrics.map((entry, i) => (
                  <MetricRow
                    key={entry.id}
                    entry={entry}
                    index={i}
                    metricDropdown={metricDropdown}
                    canRemove={state.metrics.length > 1}
                    showSeriesControls
                    seriesColor={SERIES_COLORS[i % SERIES_COLORS.length]}
                    getDefaultLabel={getMetricBaseName}
                    onChange={(patch) => dispatch({ type: "UPDATE_METRIC", index: i, patch })}
                    onRemove={() => dispatch({ type: "REMOVE_METRIC", index: i })}
                  />
                ))}
              </div>
              <Button
                type="text"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => dispatch({ type: "ADD_METRIC" })}
                style={{ fontWeight: 600, color: "#1677ff", paddingLeft: 0 }}
              >
                Add Metric
              </Button>
            </div>
          ) : (
            // Subject series: subject rows with color/fill/rename controls
            <div className="awm-step-body">
              <Text type="secondary" style={{ fontSize: 11 }}>
                Each series compares one subject across all metrics. Set a color, fill, or custom label.
              </Text>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {state.subjects.map((s, idx) => (
                  <SubjectRow
                    key={idx}
                    index={idx}
                    entry={s}
                    fallbackColor={SERIES_COLORS[idx % SERIES_COLORS.length]}
                    getSubjectEntities={getSubjectEntities}
                    canRemove={state.subjects.length > 1}
                    allowedTypes={seriesAllowedTypes}
                    showSeriesControls
                    onChange={(patch) => dispatch({ type: "UPDATE_SUBJECT", index: idx, patch })}
                    onRemove={() => dispatch({ type: "REMOVE_SUBJECT", index: idx })}
                  />
                ))}
              </div>
              {canAddSeries && (
                <Button
                  type="text"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => dispatch({ type: "ADD_SUBJECT" })}
                  style={{ fontWeight: 600, color: "#1677ff", paddingLeft: 0 }}
                >
                  Add Series
                </Button>
              )}
            </div>
          )}
        </Panel>

        {/* Step 4: Date Range */}
        <Panel
          key="__date_range"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">4</span>
              <Text strong style={{ fontSize: 13 }}>Date Range</Text>
              <SectionSummary kind="date" controllerId={state.dateControllerId} own={dateLabel} />
            </div>
          }
        >
          <DateRangeForm
            allowLatestN
            bindable={isSectionBindable("radar", "date")}
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_DATE_RANGE", patch })}
          />
        </Panel>
      </Collapse>

      {/* Display options */}
      <div
        style={{
          padding: "12px 16px",
          borderTop: "1px solid #e8ecf0",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            padding: "8px 10px",
            border: "1px solid #e0e5eb",
            borderRadius: 8,
            background: "#fafbfc",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <Text strong style={{ fontSize: 12 }}>Scale</Text>
            <Select
              size="small"
              style={{ width: 130 }}
              value={state.normalization}
              onChange={(v) =>
                dispatch({ type: "SET_NORMALIZATION", normalization: v as "player_minmax" | "team_zscore" | "custom" })
              }
              options={[
                // % Of Max scores against each subject's own range — only meaningful when
                // metrics are the axes; hidden when entities are the axes.
                ...(isEntityVertex ? [] : [{ label: "% Of Max", value: "player_minmax" }]),
                { label: "Relative", value: "team_zscore" },
                { label: "Custom", value: "custom" },
              ]}
            />
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {getScaleHelperText(state.normalization, state.vertexDimension)}
          </Text>
          {state.normalization === "custom" && isEntityVertex && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 2 }}>
              <Text style={{ fontSize: 11, color: "#888", minWidth: 28 }}>Min</Text>
              <InputNumber
                size="small"
                style={{ width: 72 }}
                value={state.customMin}
                onChange={(v) => dispatch({ type: "SET_CUSTOM_RANGE", customMin: v ?? 0 })}
              />
              <Text style={{ fontSize: 11, color: "#888", minWidth: 28 }}>Max</Text>
              <InputNumber
                size="small"
                style={{ width: 72 }}
                value={state.customMax}
                onChange={(v) => dispatch({ type: "SET_CUSTOM_RANGE", customMax: v ?? 100 })}
              />
            </div>
          )}
          {state.normalization === "custom" && !isEntityVertex && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 2 }}>
              {state.metrics.map((m, i) => {
                if (m.metricId == null) return null;
                const invalid = m.customMin != null && m.customMax != null && m.customMin >= m.customMax;
                return (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Text ellipsis style={{ fontSize: 11, flex: 1, minWidth: 0 }}>
                      {getMetricBaseName(m.metricId)}
                    </Text>
                    <InputNumber
                      size="small"
                      style={{ width: 64 }}
                      placeholder="Min"
                      status={invalid ? "error" : undefined}
                      value={m.customMin}
                      onChange={(v) => dispatch({ type: "UPDATE_METRIC", index: i, patch: { customMin: v ?? undefined } })}
                    />
                    <InputNumber
                      size="small"
                      style={{ width: 64 }}
                      placeholder="Max"
                      status={invalid ? "error" : undefined}
                      value={m.customMax}
                      onChange={(v) => dispatch({ type: "UPDATE_METRIC", index: i, patch: { customMax: v ?? undefined } })}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );

  // ── Preview ───────────────────────────────────────────────────

  const minRequired = isEntityVertex ? MIN_ENTITY_VERTICES : MIN_METRICS;
  const currentVertexCount = isEntityVertex ? state.subjects.length : configuredMetrics.length;
  const emptyText = isEntityVertex
    ? `Select at least ${MIN_ENTITY_VERTICES} ${state.vertexDimension}s and 1 metric to preview`
    : `Select at least ${MIN_METRICS} metrics to preview`;

  const summaryText = tooFewVertices
    ? `${currentVertexCount} of ${minRequired} required ${isEntityVertex ? state.vertexDimension + "s" : "metrics"} configured`
    : `${mockVertexLabels.length} vertices · ${mockSeriesConfig.length} series · ${
        state.normalization === "player_minmax" ? "% Of Max"
        : state.normalization === "team_zscore" ? "Relative"
        : isEntityVertex ? `Custom (${state.customMin}–${state.customMax})`
        : "Custom (per metric)"
      }`;

  const preview = (
    <PreviewShell
      title={state.title || "Radar Preview"}
      titleAddon={mockRadarOptions != null ? <PreviewDisclaimer /> : undefined}
      hasContent={mockRadarOptions != null}
      emptyText={emptyText}
      summaryText={summaryText}
    >
      {mockRadarOptions && (
        <div style={{ height: "calc(65vh - 130px)", minHeight: 340 }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={mockRadarOptions}
            immutable={false}
            containerProps={{ style: { height: "100%", width: "100%" } }}
          />
        </div>
      )}
    </PreviewShell>
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
